import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, HpTag } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { Spinner } from '../../components/ui/icons';
import { fmtDateTime, fmtInt } from '../../lib/format';
import { invalidatePerformance, type BuildStatus } from './queries';

const WARNING_KEYS = ['history_incomplete', 'unmapped_stages', 'no_history_loaded'] as const;
type WarningKey = (typeof WARNING_KEYS)[number];
const isWarningKey = (w: string): w is WarningKey => (WARNING_KEYS as readonly string[]).includes(w);

/** The published snapshot every number on the page is read over: as_of, coverage, warnings, rebuild. */
export function SnapshotPanel({ status, canRun }: { status: BuildStatus | undefined; canRun: boolean }) {
  const { t } = useTranslation(['performance', 'common']);
  const rebuild = useMutation(trpc.performance.startBuild.mutationOptions({ onSuccess: invalidatePerformance }));
  const published = status?.published ?? null;
  const stats = (published?.stats ?? null) as {
    totals?: { leads?: number; reserved?: number };
    coverage?: { deals?: number; withHistory?: number };
    warnings?: string[];
  } | null;

  return (
    <section className="hp-card">
      <HpCardHeader title={t('snapshot.title')} subtitle={t('snapshot.subtitle')} icon="database">
        {status?.active ? (
          <Spinner>{t('snapshot.building', { id: status.active.id })}</Spinner>
        ) : (
          canRun && (
            <HpButton
              variant="secondary"
              icon="refresh"
              className="bo-button--sm"
              busy={rebuild.isPending}
              onClick={() => rebuild.mutate()}
            >
              {t('snapshot.rebuild')}
            </HpButton>
          )
        )}
      </HpCardHeader>
      <div className="hp-card__body bo-sections">
        <ErrorAlert error={rebuild.error} />
        {!published ? (
          <p className="bo-muted">{t('snapshot.none')}</p>
        ) : (
          <>
            <div className="bo-row">
              <span>
                <span className="bo-muted">{t('snapshot.asOf')} </span>
                <strong>{fmtDateTime(published.asOf)}</strong>
              </span>
              <span className="bo-muted">·</span>
              <span>
                <span className="bo-muted">{t('snapshot.frozenAt')} </span>
                <RelativeTime value={published.frozenAt} />
              </span>
              <span className="bo-muted">·</span>
              <span className="bo-mono bo-muted">#{published.id}</span>
              {status?.stale && <HpTag tone="warning">{t('snapshot.staleTag')}</HpTag>}
            </div>
            {stats?.totals && stats.coverage && (
              <p className="bo-muted">
                {t('snapshot.totals', {
                  since: status?.population.since ?? '',
                  leads: fmtInt(stats.totals.leads ?? 0),
                  reserved: fmtInt(stats.totals.reserved ?? 0),
                })}{' '}
                {t('snapshot.coverage', {
                  withHistory: fmtInt(stats.coverage.withHistory ?? 0),
                  deals: fmtInt(stats.coverage.deals ?? 0),
                })}
              </p>
            )}
            {(stats?.warnings ?? []).filter(isWarningKey).map((w) => (
              <p key={w} className="bo-hint">
                {t(`snapshot.warning.${w}`)}
              </p>
            ))}
            {status?.stale && <p className="bo-hint">{t('snapshot.stale')}</p>}
          </>
        )}
      </div>
    </section>
  );
}
